import React from 'react';
import PropTypes from 'prop-types';
import Avatar from '@strava/ui/Avatar';

import I18n from 'utils/I18n';
import { trackV2 } from 'utils/analytics';
import styles from './styles.scss';

function CommentBody({ body }) {
  if (typeof body === 'string') {
    return body;
  }

  // The oreder of these tokens will never change, so
  // it's ok to use `index` as a key.
  const formattedTokens = body.map((token, index) => {
    if (token.type === 'mention_token') {
      return (
        // eslint-disable-next-line react/no-array-index-key
        <a key={index} className="mentioned-athlete" href={token.path}>
          {token.text}
        </a>
      );
    }

    // eslint-disable-next-line react/no-array-index-key
    return <span key={index}>{token.text}</span>;
  });

  return <>{formattedTokens}</>;
}

CommentBody.propTypes = {
  body: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired
};

const displayNumberOfComments = (num) => `${num} Comment${num > 1 ? 's' : ''}`;

const Comment = ({
  body, // eslint-disable-line react/prop-types
  displayName, // eslint-disable-line react/prop-types
  avatar, // eslint-disable-line react/prop-types
  athleteLink, // eslint-disable-line react/prop-types
  hideAthleteLink // eslint-disable-line react/prop-types
}) => (
  <div className={styles.comment}>
    <div className={styles.avatarWrapper}>
      <Avatar
        name={displayName}
        src={avatar}
        type="athlete"
        className={styles.avatar}
      />
    </div>

    <div className={styles.commentWrapper}>
      <div className={styles.commentAuthor}>
        {hideAthleteLink ? (
          <>{displayName}</>
        ) : (
          <a href={athleteLink}>{displayName}</a>
        )}
      </div>
      <div className={styles.commentBody}>
        <CommentBody body={body} />
      </div>
    </div>
  </div>
);

const Comments = ({ comments, activityCommentUrl, analyticsData }) => (
  <div className={styles.commentsContainer}>
    {comments && comments.length > 0 ? (
      <>
        <h3>{displayNumberOfComments(comments.length)}</h3>
        <ol className={styles.commentsList}>
          {comments.map((comment) => (
            <li key={comment.id}>
              <Comment
                body={comment.body}
                displayName={comment.displayName}
                avatar={comment.avatar}
                athleteLink={comment.athleteLink}
                hideAthleteLink={comment.hideAthleteLink}
              />
            </li>
          ))}
        </ol>
        <a
          href={activityCommentUrl}
          className="btn btn-primary btn-block btn-add-comment"
          onClick={() =>
            trackV2({
              ...analyticsData,
              element: 'sign_up_to_comment',
              action: 'click'
            })
          }
        >
          {I18n.t('strava.activities.show_public.signup_to_comment')}
        </a>
      </>
    ) : (
      <div className={styles.empty}>
        <h3>{I18n.t('strava.activities.show_public.no_comments')}</h3>
        <a
          href={activityCommentUrl}
          className="btn btn-primary btn-block btn-add-comment"
          onClick={() =>
            trackV2({
              ...analyticsData,
              element: 'sign_up_to_comment_empty',
              action: 'click'
            })
          }
        >
          {I18n.t('strava.activities.show_public.signup_to_comment')}
        </a>
      </div>
    )}
  </div>
);

Comments.propTypes = {
  // TODO: declare the comment shape
  comments: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      avatar: PropTypes.string,
      // The comment body could either be a string or array of objects.
      body: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.object)
      ]),
      displayName: PropTypes.string,
      hideAthleteLink: PropTypes.boolean,
      athleteLink: PropTypes.string
    })
  ).isRequired,
  activityCommentUrl: PropTypes.string.isRequired,
  analyticsData: PropTypes.shape().isRequired
};

export default Comments;
